"use client";

import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Flame, Sparkles, XCircle } from "lucide-react";
import { Badge } from "@modernatc/ui/badge";
import { Button } from "@modernatc/ui/button";
import { Callsign } from "@modernatc/ui/callsign";
import { publicEnv } from "@/lib/env";
import type { QuizQuestion } from "../_data/quiz-bank";

interface DailyQuizProps {
  date: string;
  questions: QuizQuestion[];
}

const STORAGE_KEY = "modernatc:daily-quiz";

interface QuizState {
  /** YYYY-MM-DD of the last day the user completed the quiz. */
  lastCompleted: string | null;
  /** Running streak — days consecutively completed. */
  streak: number;
  /** Total all-time correct answers. */
  correctTotal: number;
  /** Total all-time questions attempted. */
  attemptedTotal: number;
}

const EMPTY_STATE: QuizState = {
  lastCompleted: null,
  streak: 0,
  correctTotal: 0,
  attemptedTotal: 0,
};

export function DailyQuiz({ date, questions }: DailyQuizProps) {
  const [state, setState] = useState<QuizState>(EMPTY_STATE);
  const [stateLoaded, setStateLoaded] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // Load persisted state on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) setState({ ...EMPTY_STATE, ...JSON.parse(raw) });
    } catch {
      // ignore — corrupt storage falls back to empty
    }
    setStateLoaded(true);
  }, []);

  const alreadyDoneToday = state.lastCompleted === date;

  const correctIds = useMemo(() => new Set(questions.map((q) => q.id)), [questions]);

  const score = useMemo(() => {
    if (!submitted) return 0;
    return questions.reduce(
      (acc, q) => acc + (answers[q.id] === q.answer ? 1 : 0),
      0,
    );
  }, [submitted, questions, answers]);

  function selectAnswer(qid: string, cid: string) {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [qid]: cid }));
  }

  function submit() {
    if (submitted) return;
    const correctCount = questions.reduce(
      (acc, q) => acc + (answers[q.id] === q.answer ? 1 : 0),
      0,
    );
    setSubmitted(true);
    // Update persistent state.
    const next: QuizState = {
      lastCompleted: date,
      streak: streakAfter(state, date),
      correctTotal: state.correctTotal + correctCount,
      attemptedTotal: state.attemptedTotal + questions.length,
    };
    setState(next);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
    }
    // unused — keeps the bundle slim and avoids prop drilling
    void correctIds;
  }

  const allAnswered = questions.every((q) => answers[q.id]);

  return (
    <div className="space-y-6">
      {/* Status / streak strip */}
      <div className="flex flex-wrap items-center gap-4 rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-4">
        <div className="flex items-center gap-2">
          <Flame className="size-4 text-[var(--color-amber)]" />
          <span className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
            Streak
          </span>
          <span className="text-mono text-lg font-semibold text-[var(--color-ink)]">
            {stateLoaded ? state.streak : "—"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-[var(--color-scope)]" />
          <span className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
            All-time
          </span>
          <span className="text-mono text-sm text-[var(--color-ink-dim)]">
            {stateLoaded
              ? `${state.correctTotal}/${state.attemptedTotal} correct`
              : "—"}
          </span>
        </div>
        <div className="ml-auto text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
          {date} · {questions.length} questions
        </div>
      </div>

      {/* Already-done banner */}
      {stateLoaded && alreadyDoneToday && !submitted ? (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-amber)]/30 bg-[rgb(212_158_82_/_0.06)] p-5">
          <Callsign className="text-xs text-[var(--color-amber)]">
            ▸ You already completed today&rsquo;s quiz.
          </Callsign>
          <p className="mt-2 text-sm text-[var(--color-ink-dim)]">
            Streak preserved. Come back tomorrow for five new questions, or sign
            up for spaced repetition that gives you a personalized review queue.
          </p>
          <div className="mt-3">
            <a
              href={`${publicEnv.NEXT_PUBLIC_APP_URL}/signup`}
              className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] bg-[var(--color-amber)] px-4 py-2 text-sm font-medium text-[var(--color-panel-deep)] hover:bg-[var(--color-amber-bright)]"
            >
              Sign up free
            </a>
          </div>
        </div>
      ) : null}

      {/* Questions */}
      <ol className="space-y-6">
        {questions.map((q, idx) => (
          <QuestionCard
            key={q.id}
            index={idx + 1}
            q={q}
            selected={answers[q.id]}
            submitted={submitted}
            onSelect={(cid) => selectAnswer(q.id, cid)}
          />
        ))}
      </ol>

      {/* Submit / results */}
      {!submitted ? (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-5">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-ink-dim)]">
              {allAnswered
                ? "All five answered. Submit to see your results."
                : `Answer all five questions to submit. (${
                    Object.keys(answers).length
                  }/${questions.length})`}
            </p>
            <Button onClick={submit} disabled={!allAnswered} variant="primary">
              Submit
            </Button>
          </div>
        </div>
      ) : (
        <ResultsCard
          score={score}
          total={questions.length}
          streak={state.streak}
        />
      )}
    </div>
  );
}

function streakAfter(prev: QuizState, today: string): number {
  if (!prev.lastCompleted) return 1;
  if (prev.lastCompleted === today) return prev.streak; // already counted
  // Check if prev day was yesterday.
  const t = new Date(today + "T00:00:00Z").getTime();
  const p = new Date(prev.lastCompleted + "T00:00:00Z").getTime();
  const oneDay = 24 * 60 * 60 * 1000;
  if (t - p === oneDay) return prev.streak + 1;
  // Otherwise streak resets.
  return 1;
}

function QuestionCard({
  index,
  q,
  selected,
  submitted,
  onSelect,
}: {
  index: number;
  q: QuizQuestion;
  selected: string | undefined;
  submitted: boolean;
  onSelect: (cid: string) => void;
}) {
  const correct = q.answer;
  return (
    <li className="rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-5">
      <div className="flex items-center gap-3">
        <Badge variant="muted">Q{index}</Badge>
        <span className="text-callsign text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
          {q.topic}
        </span>
      </div>
      <h3 className="mt-3 text-balance text-lg font-medium text-[var(--color-ink)]">
        {q.stem}
      </h3>
      <div className="mt-4 grid gap-2">
        {q.choices.map((c) => {
          const isSelected = selected === c.id;
          const isCorrect = c.id === correct;
          const showCorrect = submitted && isCorrect;
          const showWrong = submitted && isSelected && !isCorrect;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id)}
              disabled={submitted}
              className={`flex items-start gap-3 rounded-[var(--radius-sm)] border p-3 text-left text-sm transition ${
                showCorrect
                  ? "border-[var(--color-clear)] bg-[rgb(132_204_22_/_0.08)] text-[var(--color-ink)]"
                  : showWrong
                  ? "border-[var(--color-critical)]/60 bg-[rgb(229_89_52_/_0.05)] text-[var(--color-ink)]"
                  : isSelected
                  ? "border-[var(--color-amber)] bg-[var(--color-amber)]/8 text-[var(--color-ink)]"
                  : "border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] text-[var(--color-ink-dim)] hover:border-[var(--color-amber)]/50"
              } ${submitted ? "cursor-default" : "cursor-pointer"}`}
            >
              <span className="text-callsign mt-0.5 text-[10px] uppercase tracking-wide text-[var(--color-ink-muted)]">
                {c.id.toUpperCase()}
              </span>
              <span className="flex-1">{c.text}</span>
              {showCorrect ? (
                <CheckCircle2 className="size-4 shrink-0 text-[var(--color-clear)]" />
              ) : null}
              {showWrong ? (
                <XCircle className="size-4 shrink-0 text-[var(--color-critical)]" />
              ) : null}
            </button>
          );
        })}
      </div>
      {submitted ? (
        <div className="mt-4 rounded-[var(--radius-sm)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-3 text-sm text-[var(--color-ink-dim)]">
          {q.explanation}
          <p className="mt-2 text-callsign text-[10px] uppercase tracking-wide text-[var(--color-amber)]">
            {q.citation.doc} § {q.citation.ref}
          </p>
        </div>
      ) : null}
    </li>
  );
}

function ResultsCard({
  score,
  total,
  streak,
}: {
  score: number;
  total: number;
  streak: number;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-amber)]/30 bg-[rgb(212_158_82_/_0.06)] p-6">
      <Callsign className="text-xs text-[var(--color-amber)]">
        ▸ Results
      </Callsign>
      <div className="mt-2 flex items-baseline gap-3">
        <span className="text-4xl font-semibold text-[var(--color-amber-bright)]">
          {score}/{total}
        </span>
        <Badge variant="muted">Streak {streak}</Badge>
      </div>
      <p className="mt-3 text-sm text-[var(--color-ink-dim)]">
        Come back tomorrow for five new questions. Want a personalized review
        queue that schedules each card right before you&rsquo;d forget it?
        ModernATC&rsquo;s FSRS-backed study queue does exactly that — and the
        free tier covers the Block 1 foundations.
      </p>
      <div className="mt-4">
        <a
          href={`${publicEnv.NEXT_PUBLIC_APP_URL}/signup`}
          className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] bg-[var(--color-amber)] px-4 py-2 text-sm font-medium text-[var(--color-panel-deep)] hover:bg-[var(--color-amber-bright)]"
        >
          Start studying free
        </a>
      </div>
    </div>
  );
}
